import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Loader2, CheckCircle2, LayoutDashboard, Wand2, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtCurrency, fmtNumber, formatNumberWithSeparator } from "@/lib/format";
import { useActiveCampaign } from "@/contexts/ActiveCampaignContext";
import VendorParametersSelect from "@/components/VendorParametersSelect";
import SaveParameterDialog from "@/components/SaveParameterDialog";

const EMPTY = {
  campaign_name: "", client: "", agency: "", date_start: "", date_end: "",
  target_group: "", universe_size: "", total_budget: "", tv_budget: "", olv_budget: "",
  effective_frequency: 1, selected_curve_set_id: "", notes: "", status: "active",
  // Phase D: calculation parameters (stored in parameter_snapshot)
  tv_cprp: "", olv_ecpm: "", in_demo: 1, viewability: 1, ddf: 1.061,
};

export default function Campaigns() {
  const nav = useNavigate();
  const { activeCampaignId, setActiveCampaignId, refreshCampaigns } = useActiveCampaign();
  const [list, setList] = useState([]);
  const [curves, setCurves] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vpId, setVpId] = useState("");
  const [vpSource, setVpSource] = useState("manual");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  // Phase C: Save-Parameter-Values dialog state
  const [paramDialogOpen, setParamDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: cv }, { data: vs }] = await Promise.all([
        api.get("/campaigns"),
        api.get("/curve-sets"),
        api.get("/vendors"),
      ]);
      setList(c); setCurves(cv); setVendors(vs);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const VP_WATCH_CAMPAIGN = ["universe_size", "tv_cprp", "olv_ecpm", "in_demo", "viewability", "ddf"];
  const onPickVendor = (id, vendor) => {
    setVpId(id);
    if (!vendor) { setVpSource("manual"); return; }
    setVpSource("vendor");
    setForm((f) => ({
      ...f,
      universe_size: vendor.universe_size != null ? String(Math.round(vendor.universe_size)) : f.universe_size,
      tv_cprp: vendor.cpp != null ? String(Math.round(vendor.cpp)) : f.tv_cprp,
      olv_ecpm: vendor.ecpm != null ? String(Math.round(vendor.ecpm)) : f.olv_ecpm,
      in_demo: vendor.in_demo != null ? vendor.in_demo : f.in_demo,
      viewability: vendor.viewability != null ? vendor.viewability : f.viewability,
    }));
  };
  const setFormWithVp = (key, v) => {
    setForm((f) => ({ ...f, [key]: v }));
    if (vpSource === "vendor" && VP_WATCH_CAMPAIGN.includes(key)) setVpSource("custom");
  };

  // Auto-calculate OLV = Total − TV whenever Total or TV changes
  const setBudget = (key, v) => {
    setForm((f) => {
      const next = { ...f, [key]: v };
      const total = Number(next.total_budget) || 0;
      const tv = Number(next.tv_budget) || 0;
      next.olv_budget = total > 0 ? String(Math.max(0, total - tv)) : "";
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    // Validation
    const totalStr = String(form.total_budget ?? "").trim();
    if (totalStr === "" || !(Number(form.total_budget) > 0)) {
      toast.error("Total Budget must be greater than 0.");
      return;
    }
    const total = Number(form.total_budget);
    const tvStr = String(form.tv_budget ?? "").trim();
    if (tvStr === "" || Number.isNaN(Number(form.tv_budget))) {
      toast.error("TV Budget is required.");
      return;
    }
    const tv = Number(form.tv_budget);
    if (tv < 0) {
      toast.error("TV Budget cannot be negative.");
      return;
    }
    if (tv > total) {
      toast.error("TV Budget cannot be greater than Total Budget.");
      return;
    }
    // Always recompute OLV at submit time
    const olv = Math.max(0, total - tv);
    // Phase C+D: snapshot for historical stability + provenance
    const snapshot = {
      universe_size: form.universe_size ? Number(form.universe_size) : null,
      tv_cprp: form.tv_cprp !== "" && form.tv_cprp != null ? Number(form.tv_cprp) : null,
      olv_ecpm: form.olv_ecpm !== "" && form.olv_ecpm != null ? Number(form.olv_ecpm) : null,
      in_demo: form.in_demo !== "" && form.in_demo != null ? Number(form.in_demo) : null,
      viewability: form.viewability !== "" && form.viewability != null ? Number(form.viewability) : null,
      ddf: form.ddf !== "" && form.ddf != null ? Number(form.ddf) : null,
    };
    const basePayload = {
      ...form,
      universe_size: form.universe_size ? Number(form.universe_size) : null,
      total_budget: total,
      tv_budget: tv,
      olv_budget: olv,
      effective_frequency: Number(form.effective_frequency || 1),
      selected_curve_set_id: form.selected_curve_set_id || null,
      vendor_parameter_id: vpId || null,
      vendor_parameter_name: (vendors.find((v) => v.id === vpId) || {}).vendor_name || null,
      parameter_source_type: vpSource,
      parameter_snapshot: snapshot,
    };
    // Pop-up only when source is manual or custom (no VP linkage or it has been edited)
    if (vpSource === "manual" || vpSource === "custom") {
      // Stash payload+snapshot in state-free closure via window? Cleaner: use a ref.
      pendingSaveRef.current = { basePayload, snapshot };
      setParamDialogOpen(true);
      return;
    }
    await actuallySave(basePayload);
  };

  const pendingSaveRef = useRef(null);

  const actuallySave = async (payload) => {
    try {
      if (editingId) {
        await api.put(`/campaigns/${editingId}`, payload);
        toast.success("Campaign updated — dashboard will refresh");
      } else {
        const { data: created } = await api.post("/campaigns", payload);
        toast.success("Campaign created");
        if (!activeCampaignId) setActiveCampaignId(created.id);
      }
      setOpen(false); setForm(EMPTY); setEditingId(null);
      setVpId(""); setVpSource("manual");
      await load();
      await refreshCampaigns();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDoNotSaveVP = async () => {
    setParamDialogOpen(false);
    const p = pendingSaveRef.current; if (!p) return;
    await actuallySave(p.basePayload);
  };

  const handleSaveAsNewVP = async (vendorName) => {
    setParamDialogOpen(false);
    const p = pendingSaveRef.current; if (!p) return;
    try {
      // Create a new vendor record from the current snapshot (Phase D — all
      // calc fields are now part of the campaign snapshot).
      const { data: newVp } = await api.post("/vendors", {
        vendor_name: vendorName,
        universe_size: p.snapshot.universe_size,
        cpp: p.snapshot.tv_cprp,
        ecpm: p.snapshot.olv_ecpm,
        in_demo: p.snapshot.in_demo,
        viewability: p.snapshot.viewability,
      });
      await actuallySave({
        ...p.basePayload,
        vendor_parameter_id: newVp.id,
        vendor_parameter_name: newVp.vendor_name,
        parameter_source_type: "vendor",
      });
      toast.success(`New Vendor Parameters "${vendorName}" created`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleReplaceExistingVP = async () => {
    setParamDialogOpen(false);
    const p = pendingSaveRef.current; if (!p) return;
    if (!vpId) return;
    try {
      const existing = vendors.find((v) => v.id === vpId) || {};
      await api.put(`/vendors/${vpId}`, {
        vendor_name: existing.vendor_name,
        target_audience: existing.target_audience ?? null,
        universe_size: p.snapshot.universe_size,
        cpp: p.snapshot.tv_cprp ?? existing.cpp ?? null,
        ecpm: p.snapshot.olv_ecpm ?? existing.ecpm ?? null,
        affinity: existing.affinity ?? null,
        in_demo: p.snapshot.in_demo ?? existing.in_demo ?? null,
        viewability: p.snapshot.viewability ?? existing.viewability ?? null,
      });
      await actuallySave({
        ...p.basePayload,
        parameter_source_type: "vendor", // back to vendor-aligned after replace
      });
      toast.success(`Vendor Parameters "${existing.vendor_name}" updated`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleCancelParamDialog = () => {
    setParamDialogOpen(false);
    pendingSaveRef.current = null;
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this campaign?")) return;
    await api.delete(`/campaigns/${id}`);
    toast.success("Deleted");
    if (id === activeCampaignId) setActiveCampaignId("");
    load();
    refreshCampaigns();
  };

  const onEdit = (c) => {
    const snap = c.parameter_snapshot || {};
    setForm({
      ...EMPTY, ...c,
      universe_size: c.universe_size ?? snap.universe_size ?? "",
      total_budget: c.total_budget ?? "",
      tv_budget: c.tv_budget ?? "",
      olv_budget: c.olv_budget ?? "",
      selected_curve_set_id: c.selected_curve_set_id || "",
      // Phase D: restore calc params from snapshot
      tv_cprp: snap.tv_cprp ?? snap.cpp ?? "",
      olv_ecpm: snap.olv_ecpm ?? snap.ecpm ?? "",
      in_demo: snap.in_demo ?? 1,
      viewability: snap.viewability ?? 1,
      ddf: snap.ddf ?? 1.061,
    });
    // Phase C: restore VP linkage from saved record
    setVpId(c.vendor_parameter_id || "");
    setVpSource(c.parameter_source_type || (c.vendor_parameter_id ? "vendor" : "manual"));
    setEditingId(c.id);
    setOpen(true);
  };

  const onNew = () => {
    setForm(EMPTY);
    setVpId(""); setVpSource("manual");
    setEditingId(null);
    setOpen(true);
  };

  const setActive = (id, name) => {
    setActiveCampaignId(id);
    toast.success(`"${name}" set as active campaign`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Campaigns</h2>
          <p className="text-sm text-slate-500 mt-1">Campaigns are the master source for the Dashboard.</p>
        </div>
        <button onClick={onNew} className="btn-primary" data-testid="new-campaign-btn">
          <Plus className="h-4 w-4" /> New Campaign
        </button>
      </div>

      <div className="card-harmony overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></div>
        ) : list.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-sm">
            <div>No campaigns yet. Create one to populate the Dashboard.</div>
            <button onClick={onNew} className="btn-primary mt-4">
              <Plus className="h-4 w-4" /> New Campaign
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left py-3 px-4">Campaign</th>
                <th className="text-left py-3 px-4">Client</th>
                <th className="text-left py-3 px-4">Curve Set</th>
                <th className="text-left py-3 px-4">Target</th>
                <th className="text-right py-3 px-4">Universe</th>
                <th className="text-right py-3 px-4">Total Budget</th>
                <th className="text-right py-3 px-4">TV / OLV</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => {
                const cs = curves.find((cv) => cv.id === c.selected_curve_set_id);
                const isActive = c.id === activeCampaignId;
                return (
                  <tr key={c.id} className={`border-t border-slate-100 hover:bg-slate-50 ${isActive ? "bg-[#0A3D91]/5" : ""}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {isActive && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />}
                        <button
                          onClick={() => nav(`/campaigns/${c.id}`)}
                          className="font-medium text-slate-900 hover:text-[#0A3D91] hover:underline text-left"
                          data-testid={`campaign-name-${c.id}`}
                        >
                          {c.campaign_name}
                        </button>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.agency}</div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{c.client}</td>
                    <td className="py-3 px-4">
                      {cs ? (
                        <span className="text-xs text-[#0A3D91] font-medium">{cs.curve_name}</span>
                      ) : (
                        <span className="text-xs text-amber-600">— not linked</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-slate-600">{c.target_group}</td>
                    <td className="py-3 px-4 text-right text-slate-600">{fmtNumber(c.universe_size)}</td>
                    <td className="py-3 px-4 text-right font-medium">{c.currency || "IDR"} {fmtCurrency(c.total_budget)}</td>
                    <td className="py-3 px-4 text-right text-xs text-slate-500">
                      {fmtCurrency(c.tv_budget)} / {fmtCurrency(c.olv_budget)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">
                        {c.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      {!isActive && (
                        <button onClick={() => setActive(c.id, c.campaign_name)}
                          className="inline-flex items-center gap-1 text-xs text-[#0E73C9] hover:text-[#0A3D91] font-medium mr-3"
                          data-testid={`set-active-${c.id}`}>
                          <Star className="h-3.5 w-3.5" /> Set Active
                        </button>
                      )}
                      <button onClick={() => { setActive(c.id, c.campaign_name); nav("/"); }}
                        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-[#0A3D91] font-medium mr-3"
                        data-testid={`open-dashboard-${c.id}`}>
                        <LayoutDashboard className="h-3.5 w-3.5" /> Dashboard
                      </button>
                      <button onClick={() => { setActive(c.id, c.campaign_name); nav(`/optimizer?campaign=${c.id}`); }}
                        className="inline-flex items-center gap-1 text-xs text-[#0B8F80] hover:text-[#0A3D91] font-medium mr-3"
                        data-testid={`open-optimizer-${c.id}`}>
                        <Wand2 className="h-3.5 w-3.5" /> Optimizer
                      </button>
                      <button onClick={() => onEdit(c)} className="text-[#0E73C9] hover:text-[#0A3D91] mr-3"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => onDelete(c.id)} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Campaign" : "New Campaign"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <Field label="Campaign Name *" value={form.campaign_name} onChange={(v) => setForm({ ...form, campaign_name: v })} required colSpan="2" />
            <Field label="Client" value={form.client} onChange={(v) => setForm({ ...form, client: v })} />
            <Field label="Agency" value={form.agency} onChange={(v) => setForm({ ...form, agency: v })} />
            <Field label="Start Date" type="date" value={form.date_start} onChange={(v) => setForm({ ...form, date_start: v })} />
            <Field label="End Date" type="date" value={form.date_end} onChange={(v) => setForm({ ...form, date_end: v })} />
            <Field label="Target Group" value={form.target_group} onChange={(v) => setForm({ ...form, target_group: v })} />
            <Field label="Universe Size" type="number" value={form.universe_size} onChange={(v) => setFormWithVp("universe_size", v)} format="integer" />
            <div className="col-span-2">
              <VendorParametersSelect
                vendors={vendors}
                selectedId={vpId}
                sourceType={vpSource}
                onChange={onPickVendor}
                testId="campaign-vp-select"
              />
            </div>
            <div className="col-span-2 grid grid-cols-2 gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="col-span-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center justify-between">
                <span>Calculation Parameters</span>
                <span className="text-[10px] text-slate-400 normal-case font-normal">Used for Dashboard reach calculations</span>
              </div>
              <Field label="TV CPRP" type="number" value={form.tv_cprp} onChange={(v) => setFormWithVp("tv_cprp", v)} format="integer" />
              <Field label="OLV eCPM" type="number" value={form.olv_ecpm} onChange={(v) => setFormWithVp("olv_ecpm", v)} format="integer" />
              <Field label="In-Demo (0-1)" type="number" value={form.in_demo} onChange={(v) => setFormWithVp("in_demo", v)} />
              <Field label="Viewability (0-1)" type="number" value={form.viewability} onChange={(v) => setFormWithVp("viewability", v)} />
              <Field label="DDF" type="number" value={form.ddf} onChange={(v) => setFormWithVp("ddf", v)} />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Selected Curve Set</label>
              <Select value={form.selected_curve_set_id || "none"} onValueChange={(v) => setForm({ ...form, selected_curve_set_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1" data-testid="campaign-curve-select"><SelectValue placeholder="Select curve set…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {curves.map((cv) => (<SelectItem key={cv.id} value={cv.id}>{cv.curve_name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Field label="Total Budget *" type="number" value={form.total_budget} onChange={(v) => setBudget("total_budget", v)} required format="integer" />
            <Field label="TV Budget *" type="number" value={form.tv_budget} onChange={(v) => setBudget("tv_budget", v)} required format="integer" />
            <Field
              label="OLV Budget"
              type="number"
              value={form.olv_budget}
              onChange={() => {}}
              readOnly
              hint="Automatically calculated from Total Budget minus TV Budget."
              colSpan="2"
              format="integer"
            />
            <Field label="Effective Frequency" type="number" value={form.effective_frequency} onChange={(v) => setForm({ ...form, effective_frequency: v })} />
            <div className="col-span-2">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Notes</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <DialogFooter className="col-span-2">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="campaign-submit">
                <CheckCircle2 className="h-4 w-4" /> {editingId ? "Update" : "Create"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <SaveParameterDialog
        open={paramDialogOpen}
        sourceType={vpSource}
        selectedVendorName={(vendors.find((v) => v.id === vpId) || {}).vendor_name}
        snapshot={pendingSaveRef.current?.snapshot}
        onDoNotSave={handleDoNotSaveVP}
        onSaveAsNew={handleSaveAsNewVP}
        onReplaceExisting={handleReplaceExistingVP}
        onCancel={handleCancelParamDialog}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, colSpan = "1", readOnly = false, hint, format }) {
  const isInteger = format === "integer";
  const handleChange = (e) => {
    const v = e.target.value;
    if (isInteger) {
      onChange(v.replace(/[^\d]/g, ""));
    } else {
      onChange(v);
    }
  };
  const displayValue = isInteger ? formatNumberWithSeparator(value) : value;
  return (
    <div className={colSpan === "2" ? "col-span-2" : ""}>
      <label className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={isInteger ? "text" : type}
        inputMode={isInteger ? "numeric" : undefined}
        autoComplete="off"
        required={required}
        readOnly={readOnly}
        disabled={readOnly}
        value={displayValue ?? ""}
        onChange={handleChange}
        className={`mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none ${readOnly ? "bg-slate-50 text-slate-600 cursor-not-allowed" : ""}`}
        data-testid={readOnly ? `field-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}-readonly` : undefined}
      />
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
